import React, { useEffect } from "react";
import Modal from "../../../../modal";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Radio } from "../../../../form-inputs/inputs/radio";
import { useNutritionPanelState } from "../hooks/use-nutrition-panel-state";
import { Select } from "../../../../form-inputs/inputs";
import { createOptions } from "../lib/helpers";
import { useModal } from "../../../../../shared/hooks/use-modal";

export const radioValues = {
    before: "before",
    after: "after",
};

export const ColumnShifterModal = ({ open, onClose }) => {
    const { open: isConfirmationModalOpen, onClose: onCloseConfirmationModal, onOpen: onOpenConfirmationModal } = useModal();
    const { handleSubmit, control, getValues } = useForm({
        defaultValues: {
            position: radioValues.before,
        },
    });
    const { state, dispatch } = useNutritionPanelState();
    const fromPanelValue = useWatch({
        control,
        name: "fromPanel",
    });
    const toPanelValue = useWatch({
        control,
        name: "toPanel",
    });
    const fromColumnValue = useWatch({
        control,
        name: "fromColumn",
    });
    const toColumnValue = useWatch({
        control,
        name: "toColumn",
    });
    const panelsFromOptions = createOptions(state.panels, "Panel").filter(({ value }) => value !== 0); // ignore 1st panel
    const fromColumnsOptions = createOptions(state.panels[fromPanelValue]?.columns, "Column");
    const toPanelOptions = createOptions(state.panels, "Panel").filter(({ value }) => value.toString() !== fromPanelValue);
    const toColumnsOptions = createOptions(state.panels[toPanelValue]?.columns, "Column");

    const isConfirmationDisabled = !fromPanelValue || !toPanelValue || !fromColumnValue || !toColumnValue;

    const onSubmit = ({ fromPanel, toPanel, fromColumn, toColumn, position }) => {
        dispatch({
            type: "SHIFT_COLUMN",
            fromPanel: Number(fromPanel),
            toPanel: Number(toPanel),
            fromColumn: Number(fromColumn),
            toColumn: Number(toColumn),
            position,
        });
        onCloseConfirmationModal();
        onClose();
    };

    const formatText = (field) => {
        return parseInt(getValues(field)) + 1;
    };

    useEffect(() => {
        document.body.classList.add("-modal-showing");
    }, [isConfirmationModalOpen]);

    return (
        <>
            <Modal
                show={isConfirmationModalOpen}
                onClose={onCloseConfirmationModal}
                transparent
            >
                <p>
                    Are you sure you want to move{" "}
                    <strong>
                        Panel {formatText("fromPanel")} - Column {formatText("fromColumn")}
                    </strong>{" "}
                    to{" "}
                    <strong>
                        Panel {formatText("toPanel")} {getValues("position").toUpperCase()} Column {formatText("toColumn")}
                    </strong>
                    ?
                </p>
                <div className="button-group -center m-t-16 p-l-16 p-r-16 p-b-8">
                    <div className="flex m-t-10">
                        <button
                            type="button"
                            className="button -no-margin"
                            onClick={onCloseConfirmationModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                            className="button -primary -no-margin"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </Modal>
            <Modal
                cssClass={`${isConfirmationModalOpen ? "invisible" : ""} column-shift`}
                show={open}
                onClose={onClose}
                transparent
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="p-l-16 p-r-16">
                        <h2>Move Nutrition Panel Column</h2>
                        <p>Select the Nutrition Panel and Column you want to move and where you want to move it.</p>
                        <h4>MOVE FROM:</h4>

                        <div className="flex form-field flex-column">
                            <label className="flex-1">Nutrition Panel</label>
                            <Controller
                                name="fromPanel"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <div className="flex-1">
                                            <Select
                                                {...field}
                                                options={panelsFromOptions}
                                            />
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        <div className="flex form-field flex-column">
                            <label className="flex-1">Column</label>
                            <Controller
                                name="fromColumn"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <div className="flex-1">
                                            <Select
                                                {...field}
                                                disabled={!fromPanelValue}
                                                options={fromColumnsOptions}
                                            />
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        <h4>MOVE TO:</h4>

                        <div className="flex form-field flex-column">
                            <label className="flex-1">Nutrition Panel</label>
                            <Controller
                                name="toPanel"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <div className="flex-1">
                                            <Select
                                                {...field}
                                                disabled={!fromPanelValue}
                                                options={toPanelOptions}
                                            />
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        <div className="flex form-field">
                            <Controller
                                name="position"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <Radio
                                            className="m-r-16"
                                            label="Before"
                                            name={field.name}
                                            value={radioValues.before}
                                            checked={field.value === radioValues.before}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    );
                                }}
                            />
                            <Controller
                                name="position"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <Radio
                                            label="After"
                                            name={field.name}
                                            value={radioValues.after}
                                            checked={field.value === radioValues.after}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    );
                                }}
                            />
                        </div>

                        <div className="flex form-field flex-column">
                            <label className="flex-1">Column</label>
                            <Controller
                                name="toColumn"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <div className="flex-1">
                                            <div className="flex-1">
                                                <Select
                                                    {...field}
                                                    disabled={!toPanelValue}
                                                    options={toColumnsOptions}
                                                />
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    </div>

                    <div className="button-group border-top -right m-t-16 p-l-16 p-r-16 p-b-8">
                        <div className="flex m-t-10 m-b-4">
                            <button
                                type="button"
                                className="button -no-margin"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={isConfirmationDisabled}
                                className="button -primary -no-margin"
                                onClick={onOpenConfirmationModal}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </form>
            </Modal>
        </>
    );
};
